// Interview — list of upcoming interviews. Click a card (or a per-action
// button) to drill into /interview/:sessionId where the actual prep / practice /
// contacts work happens.
//
// Phase 14 cleanup vs the previous version:
//   - Removed inline Deep Research button + drawer (it's now exclusively on
//     ApplicationDetail's Intel tab + JobDetail; having it here too crowded
//     the row and conflicted with the goal of a Story-Bank-adjacent overview).
//   - Removed the inline accordion expansion + sub-tab buttons. Each card now
//     just routes to the new InterviewSession detail page on click. This was
//     the user's "no dropdown / 下拉模块, click directly into the function"
//     ask.
//   - Readiness no longer rewards "has research" since that's a separate
//     view. baseline 0-60 from fit + 25 if has prep + 15 if has tailored CV.

import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Download,
  ExternalLink,
  FileText,
  Sparkles,
  Mic,
  MessageSquare,
  ChevronRight,
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import LogoSquare from '../components/ui/LogoSquare';
import Progress from '../components/ui/Progress';
import Chip, { stageVariant } from '../components/ui/Chip';
import { apiGet } from '../lib/api';
import { adaptDataResponse } from '../lib/adapt';
import type { Application } from '../lib/types';

function companySlug(s: string): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

interface TailoredItem {
  pdf_file: string;
  pdf_url: string;
  company_slug: string | null;
  company: string;
  date: string;
  mtime: number;
  bytes: number;
}
interface TailoredResponse { total: number; items: TailoredItem[] }

interface PrepArtifact {
  file: string;
  kind: 'research' | 'prep' | 'other';
  company_slug: string;
  date: string;
  url: string;
  mtime: number;
}
interface PrepListResponse { total: number; items: PrepArtifact[] }

function readinessPct(app: Application, hasPrep: boolean, hasCV: boolean): number {
  const baseline = Math.min(60, Math.round(app.score * 12));
  const bonus = (hasPrep ? 25 : 0) + (hasCV ? 15 : 0);
  return Math.min(100, baseline + bonus);
}

export default function Interview() {
  const q = useQuery({
    queryKey: ['data'],
    queryFn: async () => adaptDataResponse(await apiGet('/api/data')),
  });

  const sessions: Application[] = (q.data?.applications ?? []).filter(
    (a) => a.stage === 'Interview' || a.stage === 'Phone screen',
  );

  const tailoredQ = useQuery({
    queryKey: ['cv-tailored'],
    queryFn: () => apiGet<TailoredResponse>('/api/cv/tailored'),
    staleTime: 30_000,
  });
  const prepListQ = useQuery({
    queryKey: ['interview-prep-list'],
    queryFn: () => apiGet<PrepListResponse>('/api/interview-prep/list'),
    staleTime: 30_000,
  });

  const tailoredBySlug = useMemo(() => {
    const m = new Map<string, TailoredItem>();
    for (const t of tailoredQ.data?.items ?? []) {
      if (!t.company_slug) continue;
      const prev = m.get(t.company_slug);
      if (!prev || t.mtime > prev.mtime) m.set(t.company_slug, t);
    }
    return m;
  }, [tailoredQ.data]);

  const prepBySlug = useMemo(() => {
    const m = new Map<string, PrepArtifact[]>();
    for (const p of prepListQ.data?.items ?? []) {
      if (!p.company_slug) continue;
      if (p.kind !== 'prep' && p.kind !== 'other') continue;
      const list = m.get(p.company_slug) || [];
      list.push(p);
      m.set(p.company_slug, list);
    }
    return m;
  }, [prepListQ.data]);

  return (
    <div className="page-enter">
      <PageHeader
        eyebrow="Pipeline · interviews"
        title={
          <>
            {sessions.length || 'No'} upcoming.{' '}
            <em style={{ fontStyle: 'italic', color: 'var(--pri)' }}>Show up ready.</em>
          </>
        }
        sub="Each session links to your CV, evaluation report, prep pack, practice questions, and outreach drafts. Everything runs locally via Ollama."
      />

      {q.isLoading && <div className="empty">Loading sessions…</div>}
      {!q.isLoading && sessions.length === 0 && (
        <div className="card empty">
          <span className="serif">No upcoming interviews.</span>
          Move a pipeline row to <code>Interview</code> or <code>Phone screen</code> on the
          Applications page to see it here.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {sessions.map((s) => {
          const id = s.id || String(s.num);
          const slug = companySlug(s.company);
          const tailored = tailoredBySlug.get(slug) || null;
          const prepArtifacts = prepBySlug.get(slug) || [];
          const latestPrep = prepArtifacts.sort((a, b) => b.mtime - a.mtime)[0] || null;
          return (
            <InterviewSessionCard
              key={id}
              session={s}
              sessionId={id}
              tailoredPdf={tailored}
              hasPrep={!!latestPrep}
            />
          );
        })}
      </div>
    </div>
  );
}

function InterviewSessionCard({
  session,
  sessionId,
  tailoredPdf,
  hasPrep,
}: {
  session: Application;
  sessionId: string;
  tailoredPdf: TailoredItem | null;
  hasPrep: boolean;
}) {
  const nav = useNavigate();
  const readiness = readinessPct(session, hasPrep, !!tailoredPdf);
  const readinessColor = readiness >= 70 ? 'var(--good)' : readiness >= 40 ? 'var(--ink)' : 'var(--warn)';

  const goToTab = (tab: 'prep' | 'practice' | 'contacts') => {
    nav(`/interview/${encodeURIComponent(sessionId)}?tab=${tab}`);
  };

  return (
    <div className="card" style={{ padding: 0 }}>
      <div
        style={{ padding: '18px 22px', display: 'flex', alignItems: 'flex-start', gap: 16, cursor: 'pointer' }}
        onClick={() => goToTab('prep')}
      >
        <LogoSquare name={session.company} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 15, fontWeight: 600 }}>{session.company}</div>
            <Chip variant={stageVariant(session.stage)} style={{ fontSize: 10.5 }}>
              {session.stage}
            </Chip>
            <div style={{ fontSize: 11, color: 'var(--ink3)' }}>{session.date}</div>
            <div className="num" style={{ fontSize: 13, color: 'var(--ink2)' }}>
              fit {session.score.toFixed(1)}/5
            </div>
          </div>
          <div style={{ fontSize: 13.5, color: 'var(--ink2)', marginTop: 3 }}>{session.role}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 10 }}>
            <div style={{ flex: 1, maxWidth: 360 }}>
              <Progress value={readiness} color={readinessColor} />
            </div>
            <div className="num" style={{ fontSize: 13, color: readinessColor, minWidth: 40 }}>
              {readiness}%
            </div>
            <div style={{ fontSize: 10.5, color: 'var(--ink3)' }}>
              {hasPrep ? '✓ prep' : '· no prep'}{' · '}
              {tailoredPdf ? '✓ CV' : '· no tailored CV'}
            </div>
          </div>
        </div>
        <ChevronRight size={16} style={{ color: 'var(--ink3)', alignSelf: 'center' }} />
      </div>

      {/* Direct entry buttons — click a function, land in that tab */}
      <div
        style={{ padding: '0 22px 14px 78px', display: 'flex', gap: 8, flexWrap: 'wrap' }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          className="btn accent"
          onClick={() => goToTab('prep')}
          style={{ fontSize: 11.5, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 5 }}
        >
          <Sparkles size={11} /> Prep pack {hasPrep && <span style={{ color: 'var(--good)' }}>✓</span>}
        </button>
        <button
          className="btn"
          onClick={() => goToTab('practice')}
          style={{ fontSize: 11.5, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 5 }}
          title="Practice the position-specific questions with timer + STAR+R drafting"
        >
          <Mic size={11} /> Practice
        </button>
        <button
          className="btn"
          onClick={() => goToTab('contacts')}
          style={{ fontSize: 11.5, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 5 }}
        >
          <MessageSquare size={11} /> Contacts
        </button>
        <div style={{ flex: 1 }} />
        {session.url && (
          <a
            className="btn ghost"
            href={session.url}
            target="_blank"
            rel="noreferrer"
            style={{ fontSize: 11.5, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 5 }}
            onClick={(e) => e.stopPropagation()}
          >
            <ExternalLink size={11} /> Posting
          </a>
        )}
        {tailoredPdf && (
          <a
            className="btn ghost"
            href={tailoredPdf.pdf_url}
            download={tailoredPdf.pdf_file}
            style={{ fontSize: 11.5, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 5 }}
            title={`${tailoredPdf.pdf_file} · ${tailoredPdf.date}`}
          >
            <Download size={11} /> Tailored CV
          </a>
        )}
        {session.report_file && (
          <button
            className="btn ghost"
            onClick={() => nav(`/apps/${encodeURIComponent(sessionId)}`)}
            style={{ fontSize: 11.5, padding: '4px 10px', display: 'inline-flex', alignItems: 'center', gap: 5 }}
          >
            <FileText size={11} /> Evaluation
          </button>
        )}
      </div>
    </div>
  );
}
